/**
 * Toast message constants for withdrawal iframe communication
 * These messages are sent to the parent window and displayed as toast notifications
 */

export const WITHDRAWAL_TOAST_MESSAGES = {
  // Success messages (type: positive)
  withdrawalSuccess: 'Your withdrawal request was successful.',
  withdrawalSuccessOneVoucher: 'Withdraw Request Successful! A 1voucher pin will be sent to you via sms',
  withdrawalOffline: 'Your withdrawal request has been submitted and will be processed when {method} is back online.',
  
  // Error messages (type: danger)
  insufficientFunds: 'Insufficient funds',
  bankVerificationFailed: 'Verification failed. Please update accordingly',
  apiNonSuccessResponse: 'ERROR: {responseMessage}',
  withdrawalProcessingError: 'ERROR: Error processing withdrawal',
  withdrawalValidationError: 'ERROR: Error validating punter withdrawal for OTP',
  
  // Generic error with status code prefix
  apiErrorWithStatus: 'ERROR {status}: {message}',
  unauthorizedError: 'ERROR 401 (Unauthorised): {message}',
  networkError: 'ERROR: {message}',
} as const;

/**
 * Check if a withdrawal method is 1Voucher-based
 */
export const isOneVoucherMethod = (methodName: string): boolean => {
  return methodName === '1Voucher Pin';
};

/**
 * Format success message based on withdrawal type
 */
export const getSuccessToastMessage = (methodName: string, isOffline: boolean): string => {
  // Offline withdrawals
  if (isOffline) {
    const message = WITHDRAWAL_TOAST_MESSAGES.withdrawalOffline.replace('{method}', methodName.toUpperCase());
    return message;
  }  
  // 1Voucher methods
  if (isOneVoucherMethod(methodName)) {
    return WITHDRAWAL_TOAST_MESSAGES.withdrawalSuccessOneVoucher;
  }
  
  // Standard success
  return WITHDRAWAL_TOAST_MESSAGES.withdrawalSuccess;
};

/**
 * Format error message with appropriate prefix
 * Applies ERROR prefix rules based on HTTP status and error type
 */
export const getErrorToastMessage = (
  error: any,
  httpStatus?: number,
  fallbackMessage?: string
): string => {
  // Extract message from various possible error structures
  const extractMessage = () => {
    // Try parsed responsePayload first
    if (error?.data?.responsePayload) {
      const parsed = typeof error.data.responsePayload === 'string' 
        ? JSON.parse(error.data.responsePayload) 
        : error.data.responsePayload;
      
      if (parsed?.responseMessage) {
        return parsed.responseMessage;
      }
    }
    
    // Try direct responsePayload
    if (error?.responsePayload) {
      const parsed = typeof error.responsePayload === 'string'
        ? JSON.parse(error.responsePayload)
        : error.responsePayload;
      
      if (parsed?.responseMessage) {
        return parsed.responseMessage;
      }
    }
    
    // Try other common error message locations
    return error?.data?.message || error?.message || fallbackMessage || 'Request failed';
  };
  
  const message = extractMessage();
  
  // Check for specific error patterns (client-side validation - no prefix)
  if (message.toLowerCase().includes('insufficient funds') || 
      message.toLowerCase().includes('insufficient balance')) {
    return WITHDRAWAL_TOAST_MESSAGES.insufficientFunds;
  }
  
  if (message.toLowerCase().includes('verification failed') ||
      message.toLowerCase().includes('not verified')) {
    return WITHDRAWAL_TOAST_MESSAGES.bankVerificationFailed;
  }
  
  // Handle 401 Unauthorised with special format
  if (httpStatus === 401) {
    return WITHDRAWAL_TOAST_MESSAGES.unauthorizedError.replace('{message}', message);
  }
  
  // Handle API response errors with status code prefix (non-401, non-200)
  if (httpStatus && httpStatus !== 200) {
    return WITHDRAWAL_TOAST_MESSAGES.apiErrorWithStatus
      .replace('{status}', httpStatus.toString())
      .replace('{message}', message);
  }
  
  // Handle network/unknown errors (no status available)
  if (!httpStatus) {
    return WITHDRAWAL_TOAST_MESSAGES.networkError.replace('{message}', message);
  }
  
  // Handle API business logic errors (status 200 but responseMessage indicates error)
  // Generic API error response (with ERROR prefix)
  return WITHDRAWAL_TOAST_MESSAGES.apiNonSuccessResponse.replace('{responseMessage}', message);
}
